import { useEffect, useRef, useState, useCallback } from 'react'
import type { DragEndEvent } from '@dnd-kit/core'
import { FloorPlanCanvas } from './FloorPlanCanvas'
import { TableDetailPanel } from './TableDetailPanel'
import { ShapePicker } from './ShapePicker'
import { AREA_WIDTH, AREA_HEIGHT, AREA_HEADER } from './AreaLayer'
import { getShape } from './TableShapeRenderer'
import { useFloorPlanStore } from '@/lib/store/useFloorPlanStore'
import { useAreas, useCreateArea } from '@/lib/queries/useAreas'
import { useTables, useCreateTable, useBatchUpdateTablePositions } from '@/lib/queries/useTables'
import { useTableCombinations } from '@/lib/queries/useTables'
import { useAppStore } from '@/lib/store/useAppStore'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import type { TableShape } from '@/lib/types/table'
import { Plus, Save, ZoomIn, ZoomOut, LayoutGrid } from 'lucide-react'

// ─── Free-position helper (same as FloorPlanPage) ──────────────────────────

function findFreePosition(
  areaTables: Array<{ id: number; seatsMax: number; shape?: TableShape }>,
  currentPositions: Record<string, { x: number; y: number }>,
  newDims: { width: number; height: number },
  areaW: number,
  areaH: number,
  gs: number,
): { x: number; y: number } {
  for (let y = gs; y + newDims.height + gs <= areaH; y += gs) {
    for (let x = gs; x + newDims.width + gs <= areaW; x += gs) {
      const blocked = areaTables.some((t) => {
        const tPos = currentPositions[String(t.id)]
        if (!tPos) return false
        const { width: tw, height: th } = getShape(t.seatsMax, t.shape)
        return (
          x < tPos.x + tw &&
          x + newDims.width > tPos.x &&
          y < tPos.y + th &&
          y + newDims.height > tPos.y
        )
      })
      if (!blocked) return { x, y }
    }
  }
  return { x: gs, y: gs }
}

// ─── Component ─────────────────────────────────────────────────────────────

export function OnboardingFloorPlanEditor() {
  const { restaurantId } = useAppStore()
  const {
    positions,
    zoom,
    gridSize,
    dirtyIds,
    setPosition,
    selectArea,
    selectTable,
    selectedAreaId,
    selectedTableId,
    setZoom,
    initPositions,
    clearDirty,
    setMode,
  } = useFloorPlanStore()

  const { data: areasResp } = useAreas()
  const { data: tablesResp } = useTables()
  const { data: combosResp } = useTableCombinations()
  const areas = areasResp?.data ?? []
  const tables = tablesResp?.data ?? []
  const combinations = combosResp?.data ?? []

  const createArea = useCreateArea()
  const createTable = useCreateTable()
  const batchSave = useBatchUpdateTablePositions()

  // Force layout mode while in onboarding
  useEffect(() => {
    setMode('layout')
  }, [setMode])

  // ── Position init — simplified (no layout overrides) ─────────────────────
  const prevKeyRef = useRef('')
  useEffect(() => {
    if (tables.length === 0) return
    const key = tables.map((t) => t.id).sort().join(',')
    if (key === prevKeyRef.current) return
    prevKeyRef.current = key

    const snapTo = (v: number) => Math.round(v / gridSize) * gridSize
    const posMap: Record<string, { x: number; y: number }> = {}

    for (const t of tables) {
      const meta = t.metadata as { posX?: number; posY?: number } | null
      if (meta?.posX != null && meta?.posY != null) {
        posMap[String(t.id)] = { x: snapTo(meta.posX), y: snapTo(meta.posY) }
      } else {
        const idx = tables.filter((tt) => tt.area?.id === t.area?.id).indexOf(t)
        posMap[String(t.id)] = {
          x: snapTo(Math.min(40 + (idx % 4) * 120, AREA_WIDTH - 100)),
          y: snapTo(Math.min(40 + Math.floor(idx / 4) * 100, AREA_HEIGHT - AREA_HEADER - 80)),
        }
      }
    }

    // Preserve unsaved drag positions
    const { dirtyIds: curDirty, positions: curPos } = useFloorPlanStore.getState()
    for (const id of curDirty) {
      if (curPos[id]) posMap[id] = curPos[id]
    }

    initPositions(posMap)
  }, [tables, gridSize, initPositions])

  // ── Drag end — simplified (no combo logic for onboarding) ─────────────────
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, delta } = event
      if (!delta) return
      const id = String(active.id)

      if (id.startsWith('combo-')) {
        const data = active.data.current as { areaId?: string; tableIds?: number[] }
        for (const tableId of data?.tableIds ?? []) {
          const cur = positions[String(tableId)] ?? { x: 0, y: 0 }
          setPosition(String(tableId), { x: cur.x + delta.x / zoom, y: cur.y + delta.y / zoom }, data?.areaId)
        }
        return
      }

      const areaId = (active.data.current as any)?.areaId as string | undefined
      const current = positions[id] ?? { x: 0, y: 0 }
      setPosition(id, { x: current.x + delta.x / zoom, y: current.y + delta.y / zoom }, areaId)
    },
    [positions, zoom, setPosition],
  )

  // ── Save dirty positions ───────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    if (batchSave.isPending || dirtyIds.size === 0) return
    const updates = Array.from(dirtyIds)
      .map((id) => {
        const table = tables.find((t) => String(t.id) === id)
        if (!table) return null
        return {
          documentId: table.documentId,
          metadata: {
            posX: Math.round(positions[id]?.x ?? 0),
            posY: Math.round(positions[id]?.y ?? 0),
            rotation: 0,
          },
        }
      })
      .filter(Boolean) as Array<{ documentId: string; metadata: { posX: number; posY: number; rotation: number } }>
    batchSave.mutate(updates, { onSuccess: () => clearDirty() })
  }, [batchSave, dirtyIds, tables, positions, clearDirty])

  // ── Add Area modal ─────────────────────────────────────────────────────────
  const [showAddArea, setShowAddArea] = useState(false)
  const [newAreaName, setNewAreaName] = useState('')

  const handleAddArea = () => {
    if (!newAreaName.trim()) return
    createArea.mutate(
      {
        bookosId: `a-${Date.now()}`,
        name: newAreaName.trim(),
        bookable: true,
        bookableOnline: true,
        bookingPriority: 5,
        restaurant: restaurantId ?? undefined,
      } as any,
      {
        onSuccess: () => {
          setShowAddArea(false)
          setNewAreaName('')
        },
      },
    )
  }

  // ── Add Table modal ────────────────────────────────────────────────────────
  const [showAddTable, setShowAddTable] = useState(false)
  const [addTableAreaId, setAddTableAreaId] = useState('')
  const [newTableName, setNewTableName] = useState('')
  const [newTableSeats, setNewTableSeats] = useState('4')
  const [newTableShape, setNewTableShape] = useState<TableShape>('square')

  const handleAddTable = () => {
    if (!newTableName.trim() || !addTableAreaId) return
    const areaTables = tables.filter((t) => String(t.area?.id) === addTableAreaId)
    const newDims = getShape(Number(newTableSeats), newTableShape)
    const { positions: curPositions, areaBounds: curAreaBounds, gridSize: gs } = useFloorPlanStore.getState()
    const bounds = curAreaBounds[addTableAreaId] ?? { width: AREA_WIDTH, height: AREA_HEIGHT - AREA_HEADER }
    const freePos = findFreePosition(areaTables, curPositions, newDims, bounds.width, bounds.height, gs)

    createTable.mutate(
      {
        bookosId: `t-${Date.now()}`,
        name: newTableName.trim(),
        seatsMin: 1,
        seatsMax: Number(newTableSeats),
        bookable: true,
        bookableOnline: true,
        bookingPriority: 5,
        shape: newTableShape,
        type: 'single',
        deleted: false,
        area: Number(addTableAreaId),
        metadata: { posX: freePos.x, posY: freePos.y, rotation: 0 },
      } as any,
      {
        onSuccess: (result) => {
          const newTable = (result as any).data ?? result
          if (newTable?.id != null) {
            useFloorPlanStore.getState().setPosition(String(newTable.id), freePos)
          }
          setShowAddTable(false)
          setNewTableName('')
          setNewTableSeats('4')
          setNewTableShape('square')
          setAddTableAreaId('')
        },
      },
    )
  }

  const handleAreaClick = useCallback(
    (areaId: string) => {
      selectArea(selectedAreaId === areaId ? null : areaId)
    },
    [selectArea, selectedAreaId],
  )

  const areaOptions = areas.map((a) => ({ value: String(a.id), label: a.name }))
  const selectedTable = tables.find((t) => String(t.id) === selectedTableId) ?? null

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] shrink-0">
        <Button size="sm" onClick={() => setShowAddArea(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Area
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            if (areas.length === 0) return
            if (areas.length === 1) {
              setAddTableAreaId(String(areas[0].id))
            }
            setShowAddTable(true)
          }}
          disabled={areas.length === 0}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Table
        </Button>

        <div className="flex items-center gap-1 ml-auto">
          {dirtyIds.size > 0 && (
            <Button size="sm" onClick={handleSave} loading={batchSave.isPending}>
              <Save className="h-3.5 w-3.5 mr-1" /> Save layout
            </Button>
          )}
          <button
            type="button"
            onClick={() => setZoom(Math.max(0.25, zoom - 0.1))}
            className="h-7 w-7 flex items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <span className="text-xs text-[var(--color-text-muted)] w-10 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setZoom(Math.min(2, zoom + 0.1))}
            className="h-7 w-7 flex items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 overflow-hidden">
        {areas.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center gap-3 p-8">
            <div className="flex h-14 w-14 items-center justify-center rounded-[var(--radius-xl)] bg-[var(--color-bg-elevated)]">
              <LayoutGrid className="h-7 w-7 text-[var(--color-text-muted)]" />
            </div>
            <p className="text-sm text-[var(--color-text-secondary)]">
              Start by adding an area (e.g. "Main Dining", "Terrace")
            </p>
            <Button size="sm" onClick={() => setShowAddArea(true)}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add your first area
            </Button>
          </div>
        ) : (
          <FloorPlanCanvas
            areas={areas}
            bookings={[]}
            combinations={combinations}
            onDragEnd={handleDragEnd}
            onAreaClick={handleAreaClick}
          />
        )}
      </div>

      <TableDetailPanel
        table={selectedTable}
        currentBooking={null}
        combinations={combinations}
        onClose={() => selectTable(null)}
        onNewBooking={() => {}}
      />

      {/* Add Area Modal */}
      <Modal
        open={showAddArea}
        onClose={() => { setShowAddArea(false); setNewAreaName('') }}
        title="Add Area"
      >
        <div className="space-y-4">
          <Input
            label="Area Name"
            placeholder="e.g. Main Dining, Terrace, Bar"
            value={newAreaName}
            onChange={(e) => setNewAreaName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddArea()}
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => { setShowAddArea(false); setNewAreaName('') }}>
              Cancel
            </Button>
            <Button onClick={handleAddArea} loading={createArea.isPending} disabled={!newAreaName.trim()}>
              Create Area
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Table Modal */}
      <Modal
        open={showAddTable}
        onClose={() => { setShowAddTable(false); setNewTableName(''); setAddTableAreaId('') }}
        title="Add Table"
      >
        <div className="space-y-4">
          {areas.length > 1 && (
            <Select
              label="Area"
              value={addTableAreaId}
              onChange={(e) => setAddTableAreaId(e.target.value)}
              options={areaOptions}
            />
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Table Name"
              placeholder="e.g. T1, Table 5"
              value={newTableName}
              onChange={(e) => setNewTableName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddTable()}
              autoFocus
            />
            <Input
              label="Seats"
              type="number"
              min={1}
              max={20}
              value={newTableSeats}
              onChange={(e) => setNewTableSeats(e.target.value)}
            />
          </div>
          <ShapePicker value={newTableShape} onChange={setNewTableShape} />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => { setShowAddTable(false); setNewTableName(''); setAddTableAreaId('') }}>
              Cancel
            </Button>
            <Button
              onClick={handleAddTable}
              loading={createTable.isPending}
              disabled={!newTableName.trim() || !addTableAreaId}
            >
              Add Table
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
